import { useCallback, useRef } from 'react';

import { WalletMfaVerificationCancelledError } from './wallet-mfa-errors';
import { WalletMfaVerificationPurpose } from './wallet-mfa-types';

type PendingSecurityOperation = { cancelled: boolean };

async function settleUnlessCancelled<T>(
  operation: () => Promise<T>,
  pending: PendingSecurityOperation,
): Promise<T> {
  const [outcome] = await Promise.allSettled([operation()]);

  if (pending.cancelled) {
    throw new WalletMfaVerificationCancelledError();
  }

  if (outcome.status === 'rejected') {
    throw outcome.reason;
  }

  return outcome.value;
}

export const useWalletMfaOperationPurpose = () => {
  const operationRef = useRef<PendingSecurityOperation | null>(null);

  const runSecurityOperation = useCallback(async <T>(operation: () => Promise<T>): Promise<T> => {
    if (operationRef.current) {
      throw new Error('Another wallet security operation is pending.');
    }

    const pending = { cancelled: false };
    operationRef.current = pending;

    try {
      return await settleUnlessCancelled(operation, pending);
    } finally {
      if (operationRef.current === pending) {
        operationRef.current = null;
      }
    }
  }, []);

  const currentPurpose = useCallback(
    () =>
      operationRef.current
        ? WalletMfaVerificationPurpose.SecurityChange
        : WalletMfaVerificationPurpose.Transaction,
    [],
  );

  const cancelPendingSecurityOperation = useCallback(() => {
    if (operationRef.current) {
      operationRef.current.cancelled = true;
    }
  }, []);

  return { runSecurityOperation, currentPurpose, cancelPendingSecurityOperation };
};
